import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import authService from '../api/authService';
import { notification } from 'antd';

interface User {
    id: string;
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    isActive: boolean;
}

interface AuthContextType {
    isAuthenticated: boolean;
    user: User | null;
    login: (username: string, password: string) => Promise<boolean>;
    logout: () => Promise<void>;
    loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(authService.isAuthenticated());
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    // Function to handle token expiration
    const checkTokenExpiration = () => {
        if (!authService.isAuthenticated() && isAuthenticated) {
            console.log('Token expired, logging out...');

            notification.error({
                message: 'Session Expired',
                description: 'Your session has expired. Please log in again.',
                duration: 5
            });

            setIsAuthenticated(false);
            setUser(null);

            // Use window.location with the proper base path
            setTimeout(() => {
                // Get current origin and add the correct admin path
                const basePath = process.env.VITE_BASE_PATH || '/';
                window.location.href = `${window.location.origin}${basePath}login`;
            }, 2000);
        }
    };

    useEffect(() => {
        // Setup axios interceptors
        authService.setupAxiosInterceptors();

        // Load user on mount
        const loadUser = async () => {
            if (authService.isAuthenticated()) {
                try {
                    const currentUser = await authService.getCurrentUser();
                    if (currentUser) {
                        setUser({
                            id: currentUser.id,
                            username: currentUser.username,
                            email: currentUser.email,
                            firstName: currentUser.firstName,
                            lastName: currentUser.lastName,
                            isActive: currentUser.isActive
                        });
                        setIsAuthenticated(true);
                    } else {
                        setIsAuthenticated(false);
                        setUser(null);
                    }
                } catch (error) {
                    console.error('Error loading user:', error);
                    setIsAuthenticated(false);
                    setUser(null);
                }
            } else {
                setIsAuthenticated(false);
                setUser(null);
            }
            setLoading(false);
        };

        loadUser();

        // Set up periodic token expiration check
        const tokenCheckInterval = setInterval(() => {
            checkTokenExpiration();
        }, 60000); // Check every minute

        // Clean up interval when component unmounts
        return () => clearInterval(tokenCheckInterval);
    }, []);

    const login = async (username: string, password: string): Promise<boolean> => {
        setLoading(true);
        const success = await authService.login(username, password);

        if (success) {
            const currentUser = await authService.getCurrentUser();
            if (currentUser) {
                setUser({
                    id: currentUser.id,
                    username: currentUser.username,
                    email: currentUser.email,
                    firstName: currentUser.firstName,
                    lastName: currentUser.lastName,
                    isActive: currentUser.isActive
                });
                setIsAuthenticated(true);
            }
        }

        setLoading(false);
        return success;
    };

    const logout = async (): Promise<void> => {
        setLoading(true);
        await authService.logout();
        setIsAuthenticated(false);
        setUser(null);
        setLoading(false);
    };

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                user,
                login,
                logout,
                loading
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export function useAuth(): AuthContextType {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
} 